import useActiveOrdersContext from '@/contexts/activeOrders/useActiveOrdersContext'
import type { SellerNewOrderNotificationData } from '@/proxy/order/hooks/useSellerNewOrderNotification/types'
import { useSellerNewOrderNotification } from '@/proxy/order/hooks'
import { getScheduledOrdersKanban } from '@/pages/ScheduledOrders/utils'
import { OrderStatus } from '@/resources/enums'
import { NotificationType } from '@/resources/enums/orders'
import { ActiveOrder } from '@/resources/types'
import { Interval } from '@/resources/types/websocket'
import {
  getNotificationType,
  getPendingOrderNumbers,
  hasPendingOrders,
  hasTurboInSellerNewOrderNotifications,
  updateScheduledOrdersNotificationTracking,
  validatePendingOrders,
} from '@/resources/utils/orders/notification'
import { CHECK_ORDERS_POLLING_TIME } from '@/seller.application.polling.time.config'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import OrderBell, { OrderRef } from '../OrderBell'

const CheckOrders = () => {
  const {
    activeOrders,
    isFromWss,
    isQueryFetching,
    kanbanFiltersAppliedToQuery,
  } = useActiveOrdersContext()

  const { refetch: refetchNewOrderNotifications } =
    useSellerNewOrderNotification()

  const bellRef = useRef<OrderRef>(null)
  const pendingOrdersRef = useRef<string[]>([])
  const notificationIntervalRef = useRef<Interval>(null)

  const activeOrdersRef = useRef<ActiveOrder[]>([])
  const acceptedScheduledOrdersRef = useRef<ActiveOrder[]>([])

  const acceptedScheduledOrders = useMemo(
    () =>
      getScheduledOrdersKanban({
        orders: activeOrders,
        includedStatus: [OrderStatus.POC_ACCEPTED, OrderStatus.DM_ACCEPTED],
      }),
    [activeOrders]
  )

  activeOrdersRef.current = activeOrders
  acceptedScheduledOrdersRef.current = acceptedScheduledOrders

  const playBellFromNewOrderNotificationData = (
    data: SellerNewOrderNotificationData | undefined
  ) => {
    const notifications = data?.newOrderNotifications ?? []
    if (notifications.length === 0) {
      return
    }
    const pendingOrderNumbers = notifications.map(
      ({ orderNumber }) => orderNumber
    )
    bellRef.current?.playAudio(
      getNotificationType(hasTurboInSellerNewOrderNotifications(notifications)),
      pendingOrderNumbers
    )
  }

  useEffect(() => {
    if (!kanbanFiltersAppliedToQuery) {
      return
    }
    void refetchNewOrderNotifications().then(result =>
      playBellFromNewOrderNotificationData(result.data)
    )
  }, [kanbanFiltersAppliedToQuery, refetchNewOrderNotifications])

  const checkOrdersNotification = useCallback(() => {
    if (kanbanFiltersAppliedToQuery) {
      void refetchNewOrderNotifications().then(result =>
        playBellFromNewOrderNotificationData(result.data)
      )
      return
    }

    const { hasPending, hasTurbo } = hasPendingOrders(activeOrdersRef.current)

    if (hasPending) {
      bellRef.current?.playAudio(
        getNotificationType(hasTurbo),
        getPendingOrderNumbers(activeOrdersRef.current)
      )
    } else if (acceptedScheduledOrdersRef.current.length > 0) {
      const shouldPlayScheduledAudio =
        updateScheduledOrdersNotificationTracking(
          acceptedScheduledOrdersRef.current
        )
      if (shouldPlayScheduledAudio) {
        bellRef.current?.playAudio(NotificationType.SCHEDULED)
      }
    }
  }, [kanbanFiltersAppliedToQuery, refetchNewOrderNotifications])

  useEffect(() => {
    notificationIntervalRef.current = setInterval(
      checkOrdersNotification,
      CHECK_ORDERS_POLLING_TIME
    )

    return () => {
      if (notificationIntervalRef.current) {
        clearInterval(notificationIntervalRef.current)
      }
    }
  }, [checkOrdersNotification])

  useEffect(() => {
    if (kanbanFiltersAppliedToQuery) {
      return
    }

    if (isFromWss || !isQueryFetching) {
      if (!activeOrders) return

      const { hasNewPendingOrders, hasNewTurboOrders } = validatePendingOrders(
        activeOrders,
        pendingOrdersRef
      )

      if (hasNewPendingOrders) {
        bellRef.current?.playAudio(
          getNotificationType(hasNewTurboOrders),
          getPendingOrderNumbers(activeOrders)
        )
      } else if (acceptedScheduledOrders.length > 0) {
        const shouldPlayScheduledAudio =
          updateScheduledOrdersNotificationTracking(acceptedScheduledOrders)
        if (shouldPlayScheduledAudio) {
          bellRef.current?.playAudio(NotificationType.SCHEDULED)
        }
      }
    }
  }, [
    kanbanFiltersAppliedToQuery,
    isQueryFetching,
    isFromWss,
    activeOrders,
    acceptedScheduledOrders,
  ])

  return <OrderBell ref={bellRef} />
}

export default CheckOrders
